import { RestAPI, useRealTimeState, useThen } from 'scripts';
import { AttachmentType, AttachmentUsageEnum } from 'types';
import { Spin, Upload, UploadFile } from 'antd';
import styles from './upload.module.scss';
import { PiCameraLight } from 'react-icons/pi';
import { COLORS } from 'utilities';
import { API } from 'data';
import { __GalleryUploadPropsType } from './upload.type';
import { useEffect, useMemo, useRef } from 'react';
import { TbPlus } from 'react-icons/tb';

const maxImages = 20;
const maxUploadBox = 6;

type UploadFileLike = Blob & { uid: string };

function shouldReverseUploadOrder() {
    if (typeof navigator === 'undefined') return true;
    return !/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

export default function CustomUploadComp(props: __GalleryUploadPropsType) {
    const fileLists = useRealTimeState(props.initialValue ? props.initialValue.sort((a, b) => a.id - b.id) : []);
    const timerRef = useRef<NodeJS.Timeout>();
    const newUploads = useRef<{ body: FormData; name: string }[]>([]);

    const boxCount = useMemo(() => {
        return fileLists.dep.length >= maxImages
            ? 0
            : fileLists.dep.length < maxUploadBox
              ? maxUploadBox - fileLists.dep.length
              : 1;
    }, [fileLists.dep.length]);

    const onUpload = (f: UploadFileLike, filename?: string) => {
        props.uploadingChange?.(true);
        if (timerRef.current) clearTimeout(timerRef.current);
        const name = filename ?? new Date().getTime().toString();
        const body = new FormData();
        body.append('file', f);
        body.append('alt', name);
        body.append('usage', AttachmentUsageEnum.content.toString());
        newUploads.current = [...newUploads.current, { body, name }];
        timerRef.current = setTimeout(() => {
            const step = shouldReverseUploadOrder() ? -1 : 1;
            const start = step === -1 ? newUploads.current.length - 1 : 0;
            uploadList(start, step);
        }, 1000);
    };

    const uploadList = (listIndex: number, step: number) => {
        if (listIndex < 0 || listIndex >= newUploads.current.length) return;
        if (fileLists.get().length < maxImages) {
            const item = newUploads.current[listIndex];
            const list = [...fileLists.get()];
            const uid = -1 * (list.length + 1);
            list.push({ id: uid, file: item.name });
            fileLists.set(list);
            RestAPI.form<AttachmentType>(API.core.attachment.index, item.body)
                .then((res) => {
                    const newList = [...fileLists.get()];
                    const index = newList.findIndex((i) => i.id === uid);
                    if (index >= 0) newList[index] = { ...res.data };
                    fileLists.set(newList);
                    const nextIndex = listIndex + step;
                    if (nextIndex >= 0 && nextIndex < newUploads.current.length) uploadList(nextIndex, step);
                    else onFinishUpload();
                })
                .catch(() => {
                    const newList = [...fileLists.get()];
                    const index = newList.findIndex((i) => i.id === uid);
                    if (index >= 0) newList[index] = { id: 0, file: item.name };
                    fileLists.set(newList);
                    const nextIndex = listIndex + step;
                    if (nextIndex >= 0 && nextIndex < newUploads.current.length) uploadList(nextIndex, step);
                    else onFinishUpload();
                });
        }
    };

    const onFinishUpload = () => {
        props.uploadingChange?.(false);
        newUploads.current = [];
    };

    const onRemove = (e: UploadFile) => {
        const id = Number(e.uid);
        // RestAPI.delete(API.core.attachment.detail(id));
        let list1 = fileLists.get();
        list1 = list1.filter((i) => i.id !== id);
        fileLists.set(list1);
    };

    useEffect(() => {
        props.onHasImagesChange?.(
            fileLists.get().filter((f) => /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(f.file)).length > 0,
        );
    }, [fileLists.dep]);

    useThen(() => {
        props.onChange?.(fileLists.get().map((i) => i.id));
    }, [fileLists.dep]);

    return (
        <div className={`d-flex gap-16 flex-wrap ${styles['upload-container']}`}>
            {[...fileLists.dep].map((item, index) => (
                <Upload
                    key={index}
                    listType="picture-card"
                    onRemove={onRemove}
                    accept="image/*, video/*"
                    fileList={[
                        {
                            uid: item.id.toString(),
                            name: item.file.split('/')[item.file.split('/').length - 1],
                            status: item.id === 0 ? 'error' : item.id < 0 ? 'uploading' : 'done',
                            url: item.file,
                        },
                    ]}
                    customRequest={(e) => {
                        if (typeof e.file !== 'string') onUpload(e.file as UploadFileLike, e.filename);
                    }}
                    multiple={true}
                    maxCount={maxImages - fileLists.dep.length}
                    className={`${styles['upload-layout']}`}
                />
            ))}
            {[...Array(boxCount)].map((_, index) => (
                <Upload
                    key={index}
                    rootClassName="m-0"
                    listType="picture-card"
                    onRemove={onRemove}
                    accept="image/*, video/*"
                    fileList={[]}
                    customRequest={(e) => {
                        if (typeof e.file !== 'string') onUpload(e.file as UploadFileLike, e.filename);
                    }}
                    multiple={true}
                    maxCount={maxImages - fileLists.dep.length}
                    className={`${styles['upload-layout']}`}
                >
                    <div className="flex-column center-content">
                        {fileLists.dep.length < maxImages && index === boxCount - 1 ? (
                            <TbPlus color={COLORS.primary} size={48} className={styles['plus-icon']} />
                        ) : (
                            <>
                                <PiCameraLight color={COLORS.primary} size={32} />
                                <p className="mt-1 t-p2 t-primary-i">عکس یا فیلم جدید</p>
                            </>
                        )}
                    </div>
                </Upload>
            ))}
            {props.uploading ? (
                <div className={`${styles['loading-box']} center-content`}>
                    <Spin />
                </div>
            ) : null}
        </div>
    );
}
